import { TelemetryEvent, TelemetryEventBase, TelemetryEventPayloads } from '@weavix/telemetry';
import { Batcher } from '@weavix/utils/src/batcher';
import { myUserId } from 'models-mobx/my-profile-store/my-profile-store';
import { PubSubService } from './pub-sub.service';
import { Topic } from '@weavix/models/src/topic/topic';
import { Injectable, inject } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { environment } from 'environments/environment';

export interface IdentifyTraits {
    email: string;
    name: string;
    accountId: string;
    userId: string;
}

/**
 * Unlike the AnalyticsService, this only sends to New Relic and the data platform.
 * It does not send to Segment/Amplitude.
 *
 * @example
 * ```ts
 * // Inject into your service or component.
 * private readonly telemetryService = inject(TelemetryService);
 *
 * // Send events!
 * this.telemetryService.sendTelemetry(TelemetryEvent.SomeEvent, { foo: 'bar' });
 * ```
 */
@Injectable({
    providedIn: 'root',
})
export class TelemetryService {
    private readonly pubSubService = inject(PubSubService);
    private readonly deviceDetectorService = inject(DeviceDetectorService);

    private identity?: IdentifyTraits;

    private readonly batcher = new Batcher<{ name: TelemetryEvent, data: TelemetryEventBase }>(async req => {
        try {
            await this.publish(req);
        } catch (e) {
            console.warn(`Failed to publish telemetry ${e?.message}`);
        }
        return req;
    }, 60_000);

    setTraits(traits: IdentifyTraits) {
        this.identity = traits;
    }

    /** Add telemetry to batch. */
    async sendTelemetry<E extends TelemetryEvent>(name: E, data: TelemetryEventPayloads[E]) {
        this.batcher.add(this.createRequest(name, data));
    }

    /** Bypass batching and send immediately. */
    async sendTelemetryImmediate<E extends TelemetryEvent>(name: E, data: TelemetryEventPayloads[E]) {
        await this.publish([this.createRequest(name, data)]);
    }

    private createRequest<E extends TelemetryEvent>(name: E, data: TelemetryEventPayloads[E]) {
        const date = data.date ?? new Date();
        const deviceInfo = this.deviceDetectorService.getDeviceInfo();

        return {
            name,
            data: {
                ...data,
                ...this.identity,

                deviceType: deviceInfo.deviceType,
                deviceName: deviceInfo.device,

                userId: myUserId() ?? this.identity?.userId,
                accountId: data.accountId ?? this.identity?.accountId,
                source: environment.analyticsSource,
                date,
                dateMs: date instanceof Date ? date.getTime() : undefined,

                osName: deviceInfo.os,
                osVersion: deviceInfo.os_version,
                appVersion: environment.version,

                browserName: deviceInfo.browser,
                browserVersion: deviceInfo.browser_version,
                userAgent: deviceInfo.userAgent,
            },
        };
    }

    private async publish(requests: Array<{ name: TelemetryEvent, data: any }>): Promise<void> {
        const userId = myUserId();
        const topic = userId ? Topic.UserTelemetry : Topic.SelfTelemetry;
        await this.pubSubService.publish(topic, [userId ?? this.pubSubService.clientId], requests);
    }
}
